<template>
  <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 h-full flex flex-col">
    <div class="flex items-baseline justify-between gap-3 mb-4">
      <div class="min-w-0">
        <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{{ title }}</h2>
        <p v-if="subtitle" class="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{{ subtitle }}</p>
      </div>
      <span v-if="!loading && total > 0" class="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums whitespace-nowrap">
        {{ formatNumber(total) }} {{ unit }} total
      </span>
    </div>

    <!-- Loading: skeleton rows in the same rhythm as the real list -->
    <div v-if="loading" class="space-y-4" aria-busy="true" :aria-label="`Loading ${title}`">
      <div v-for="n in 5" :key="n" class="space-y-2">
        <div class="flex justify-between">
          <span class="h-3.5 w-24 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse"></span>
          <span class="h-3.5 w-20 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse"></span>
        </div>
        <div class="h-2 rounded-full bg-neutral-100 dark:bg-neutral-700/60 animate-pulse"></div>
      </div>
    </div>

    <!-- Ranked list: one hue; the order and the numbers carry the meaning. Bars are
         scaled to the largest item, the percentage is share of the total. -->
    <template v-else-if="ranked.length">
      <ol class="space-y-3.5 flex-1">
        <li v-for="(item, idx) in visible" :key="item.id ?? idx">
          <component
            :is="clickable ? 'button' : 'div'"
            :type="clickable ? 'button' : null"
            :class="[
              'w-full text-left space-y-1.5 rounded-lg -mx-2 px-2 py-1',
              clickable ? 'hover:bg-neutral-50 dark:hover:bg-neutral-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors' : '',
            ]"
            @click="clickable && $emit('select', item)"
          >
            <div class="flex items-baseline justify-between gap-3">
              <span
                :class="[
                  'text-sm truncate',
                  idx === 0 && !item.muted ? 'font-semibold text-neutral-900 dark:text-neutral-50' : 'font-medium text-neutral-700 dark:text-neutral-300',
                ]"
                :title="item.label"
              >
                {{ item.label }}
              </span>
              <span class="flex items-baseline gap-2 tabular-nums whitespace-nowrap">
                <span class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{{ formatNumber(item.value) }} {{ unit }}</span>
                <span class="text-xs text-neutral-500 dark:text-neutral-400 w-9 text-right">{{ share(item.value) }}%</span>
              </span>
            </div>
            <div class="w-full h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden" aria-hidden="true">
              <div
                :class="['h-full rounded-full transition-all duration-500 ease-out', item.muted ? 'bg-neutral-400 dark:bg-neutral-500' : 'bg-primary-500']"
                :style="{ width: barWidth(item.value) + '%' }"
              ></div>
            </div>
            <div v-if="item.count != null" class="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
              {{ formatNumber(item.count) }} {{ item.count === 1 ? countLabel.replace(/s$/, '') : countLabel }}
              <template v-if="item.secondary"> · {{ item.secondary }}</template>
            </div>
          </component>
        </li>
      </ol>

      <button
        v-if="ranked.length > maxVisible"
        type="button"
        class="mt-4 self-start text-sm font-semibold text-primary-700 dark:text-primary-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
        :aria-expanded="expanded.toString()"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Show fewer' : `Show all ${ranked.length} ${itemsLabel}` }}
      </button>
    </template>

    <div v-else class="flex items-center justify-center flex-1 min-h-64 text-neutral-400 dark:text-neutral-500">
      <div class="text-center">
        <span class="material-icons text-4xl" aria-hidden="true">{{ emptyIcon }}</span>
        <p class="text-sm mt-2">{{ emptyText }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { formatNumber } from '@/utils/format'

// Ranked single-measure comparison (zones, group members, supplier locations).
// Replaces horizontal bar charts, which collapse into 1px lines and dropped labels
// once a group has more than ~15 members.
// items: [{ id, label, value, count?, secondary?, muted? }]
export default {
  name: 'RankedBarList',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    unit: { type: String, default: 'L' },
    countLabel: { type: String, default: 'orders' },
    itemsLabel: { type: String, default: 'items' },
    loading: { type: Boolean, default: false },
    maxVisible: { type: Number, default: 6 },
    clickable: { type: Boolean, default: false },
    emptyIcon: { type: String, default: 'leaderboard' },
    emptyText: { type: String, default: 'No data available' },
  },
  emits: ['select'],
  setup(props) {
    const expanded = ref(false)

    const ranked = computed(() => [...(props.items || [])].sort((a, b) => (b.value || 0) - (a.value || 0)))
    const visible = computed(() => (expanded.value ? ranked.value : ranked.value.slice(0, props.maxVisible)))
    const total = computed(() => ranked.value.reduce((sum, i) => sum + (i.value || 0), 0))
    const maxValue = computed(() => Math.max(...ranked.value.map((i) => i.value || 0), 1))

    const barWidth = (v) => Math.round(((v || 0) / maxValue.value) * 100)
    const share = (v) => (total.value ? Math.round(((v || 0) / total.value) * 100) : 0)

    return { expanded, ranked, visible, total, barWidth, share, formatNumber }
  },
}
</script>
